import { ArrowForwardIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  SimpleGrid,
  Tag,
  TagLabel,
  Text
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'

import { ActionFab, ActionMenuItem } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { Icon } from '../../../components/Icon'
import { PageTitle } from '../../../components/PageTitle'
import { Tooltip } from '../../../components/Tooltip'
import { removeAccents, useHasRole, useAppToast } from '../../../helpers'
import GemsService from '../../../services/Gems/GemsService'
import { IGem } from '../../../services/Gems/interfaces'
import { ISector, SectorService } from '../../../services/Sector/SectorService'
import { GemsCreateModal } from './GemsCreateModal'
import { GemsEditModal } from './GemsEditModal'

export default function GemsPage() {
  const sectorService = useMemo(() => new SectorService(), [])
  const [sectors, setSectors] = useState<ISector[]>([])
  const gemsService = useMemo(() => new GemsService(), [])
  const [gems, setGems] = useState<IGem[]>([])
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingGem, setEditingGem] = useState<IGem | null>(null) // null to none, or the gem being edited
  const [deletingGem, setDeletingGem] = useState<IGem | null>(null) // null to none, or the gem being deleted

  const toast = useAppToast()
  const hasRole = useHasRole()
  const [isDeleting, setIsDeleting] = useState(false)
  const [filter, setFilter] = useState({
    search: '',
    sectorIds: [] as number[]
  })
  const permissions = {
    addAiAgents: hasRole('add_ai_agents'),
    editAiAgents: hasRole('edit_ai_agents'),
    deleteAiAgents: hasRole('delete_ai_agents')
  }
  const filteredGems = useMemo(() => {
    const searchClean = removeAccents(filter.search.toLowerCase())

    return gems.filter((gem) => {
      const gemCleanName = removeAccents(gem.name.toLowerCase())
      const gemCleanDescription = removeAccents(gem.description.toLowerCase())

      const matchesSearch =
        filter.search === '' || gemCleanName.includes(searchClean) || gemCleanDescription.includes(searchClean)

      const matchesSectors = filter.sectorIds.length === 0 || gem.sectors.some((s) => filter.sectorIds.includes(s.id))

      return matchesSearch && matchesSectors
    })
  }, [gems, filter])

  const handleDelete = async () => {
    if (!deletingGem) {
      return
    }

    setIsDeleting(true)
    try {
      await gemsService.delete(deletingGem.id)
      toast.success('Agente de IA excluído')
      setDeletingGem(null)
      fetchGems()
    } catch (error) {
      toast.error('Erro ao excluir o agente de IA', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const fetchGems = async () => {
    try {
      await gemsService.list().then((data) => {
        setGems(data.gems)
      })
    } catch {
      toast.error(
        'Falha ao carregar agentes de IA',
        'Ocorreu um erro ao buscar os agentes de IA. Tente novamente mais tarde.'
      )
    }
  }

  useEffect(() => {
    const loadSectors = async () => {
      try {
        await sectorService.listForSelect().then((data) => {
          setSectors(data)
        })
      } catch {
        toast.error('Falha ao carregar setores', 'Ocorreu um erro ao buscar os setores. Tente novamente mais tarde.')
        setSectors([])
      }
    }

    loadSectors()
    fetchGems()
  }, [])

  const handleSectorClick = (sectorId: number) => {
    setFilter((prevFilter) => {
      const isSelected = prevFilter.sectorIds.includes(sectorId)
      const newSectorIds = isSelected
        ? prevFilter.sectorIds.filter((id) => id !== sectorId)
        : [...prevFilter.sectorIds, sectorId]

      return {
        ...prevFilter,
        sectorIds: newSectorIds
      }
    })
  }

  const GemCardSectors = ({ sectors }: { sectors: { id: number; name: string }[] }) => {
    const sortedSectors = [...sectors].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
    const displaySectorsAmount = (sortedSectors[0].name.length || 0 + sortedSectors[1].name.length || 0) > 15 ? 1 : 2

    return (
      <Box display={'flex'} alignItems={'center'} gap={2} overflow="hidden" flexWrap="nowrap">
        {sortedSectors.slice(0, displaySectorsAmount).map((sector) => (
          <Tag
            key={sector.id}
            colorScheme="magenta"
            variant={'solid'}
            fontWeight={'semibold'}
            rounded={'full'}
            flexShrink={0}
          >
            {sector.name}
          </Tag>
        ))}
        {sortedSectors.length > displaySectorsAmount && (
          <Tooltip
            label={sortedSectors
              .slice(displaySectorsAmount)
              .map((s) => s.name)
              .join(', ')}
          >
            <Tag colorScheme="gray" variant={'outline'} fontWeight={'semibold'} rounded={'full'} flexShrink={0}>
              +{sortedSectors.length - displaySectorsAmount}
            </Tag>
          </Tooltip>
        )}
      </Box>
    )
  }

  return (
    <>
      {/* Header */}
      <PageTitle
        title="ABRA Gems"
        description="Selecione o Agente de IA ideal para agilizar e otimizar seus projetos e tarefas."
      />

      {/* Search Bar */}
      <InputGroup size={'lg'} maxWidth="xl" mb="8">
        <InputLeftElement pointerEvents="none">
          <Icon name="lni lni-search-alt" color="gray.400" />
        </InputLeftElement>
        <Input
          type="text"
          placeholder="Buscar por nome ou descrição..."
          value={filter.search}
          onChange={(e) => setFilter({ ...filter, search: e.target.value })}
        />
      </InputGroup>

      {/* Sectors Selector */}
      <Box display={'flex'} flexWrap={'wrap'} gap={3} mb={10}>
        {[...sectors]
          .filter((sector) => gems.some((gem) => gem.sectors.some((s) => s.id === sector.id)))
          .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
          .map((sector) => {
            const isSelected = filter.sectorIds.includes(sector.id)
            return (
              <Tag
                key={sector.id}
                size={'lg'}
                variant={`${isSelected ? 'solid' : 'outline'}`}
                colorScheme={`${isSelected ? 'magenta' : 'gray'}`}
                fontWeight={'semibold'}
                rounded="full"
                onClick={() => handleSectorClick(sector.id)}
                cursor="pointer"
              >
                <TagLabel>{sector.name}</TagLabel>
              </Tag>
            )
          })}
        {filter.sectorIds.length > 0 && (
          <Tag
            size={'lg'}
            variant={'outline'}
            colorScheme={'magenta'}
            fontWeight={'semibold'}
            rounded="full"
            onClick={() => setFilter({ ...filter, sectorIds: [] })}
            cursor="pointer"
          >
            <TagLabel>Limpar Filtros</TagLabel>
          </Tag>
        )}
      </Box>

      {/* AI Agents */}
      <SimpleGrid columns={3} spacing={10} alignItems="stretch">
        {filteredGems.map((gem) => (
          <Box
            key={gem.id}
            display={'flex'}
            flexDirection={'column'}
            gap={4}
            bg="whiteAlpha.50"
            p={6}
            borderRadius="md"
            borderTop="5px solid"
            borderColor="magenta.500"
            boxShadow="lg"
            h="300px"
          >
            <Box display={'flex'} gap={3} justifyContent={'space-between'} overflow="hidden">
              {/* Gem Name */}
              <Tooltip label={gem.name}>
                <Heading alignSelf="center" noOfLines={2}>
                  {gem.name}
                </Heading>
              </Tooltip>

              {/* Gem Menu Options */}
              {(permissions.deleteAiAgents || permissions.editAiAgents) && (
                <Menu>
                  <MenuButton as={IconButton} aria-label="Opções" size="sm" variant="ghost">
                    <Icon name="lni lni-line-dotted" transform="rotate(90deg)" />
                  </MenuButton>
                  <MenuList>
                    {permissions.editAiAgents && <ActionMenuItem action="edit" onClick={() => setEditingGem(gem)} />}
                    {permissions.deleteAiAgents && (
                      <ActionMenuItem action="delete" onClick={() => setDeletingGem(gem)} />
                    )}
                  </MenuList>
                </Menu>
              )}
            </Box>

            {/* Gem Description */}
            <Box flex={1} overflow={'hidden'}>
              <Text fontWeight={'medium'} noOfLines={3}>
                {gem.description}
              </Text>
            </Box>

            {/* Gem Sectors */}
            <GemCardSectors sectors={gem.sectors} />

            {/* Gem Button */}
            <a href={gem.url} target="_blank" rel="noopener noreferrer">
              <Button w={'full'} rightIcon={<ArrowForwardIcon />} colorScheme={'green'} rounded={'xl'} size={'lg'}>
                Acessar Gem
              </Button>
            </a>
          </Box>
        ))}
      </SimpleGrid>

      {/* Add New Gem Button */}
      {permissions.addAiAgents && <ActionFab label="Adicionar Gem" onClick={() => setIsCreateModalOpen(true)} />}

      {/* Modal's */}
      <GemsCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          fetchGems()
          setIsCreateModalOpen(false)
        }}
        sectors={sectors}
      />

      {editingGem && (
        <GemsEditModal
          isOpen={!!editingGem}
          onClose={() => {
            fetchGems()
            setEditingGem(null)
          }}
          sectors={sectors}
          gem={editingGem as IGem}
        />
      )}

      {deletingGem && (
        <ConfirmDialog
          isOpen={!!deletingGem}
          onClose={() => setDeletingGem(null)}
          onConfirm={handleDelete}
          action="delete"
          entity="agente de IA"
          name={deletingGem?.name}
          isLoading={isDeleting}
        />
      )}
    </>
  )
}
